import { type ElasticsearchQuery, type FacetFieldConfig } from '@searchkit/api';

import {
  allWNBAQuery,
  eplOnlySoccerQuery,
  noFootballQuery,
  noWorldCup26Query,
  supportedCategoryGroupsQuery,
  validBettingSplitsQuery,
} from '~/apps/product-plus/search-domain/queries';
import {
  type SearchDomainContext,
  type SearchRuntimeSortingConfig,
  type SearchUiContext,
} from '~/apps/product-plus/search-domain/types';

import { FILTERS_CONFIGS } from '../../config/filters-config';

const bettingSplitsResultAttributes = [
  'betName',
  'betPercentage',
  'categoryName',
  'categoryGroupId',
  'categoryGroupName',
  'categoryId',
  'categoryName',
  'eventDisplayName',
  'eventId',
  'eventDisplayName',
  'marketDisplayName',
  'subeventId',
  'subeventName',
  'subeventStartDate',
  'subeventStartTime',
  'teamData',
];

export function getBettingSplitsBaseFilters(context: SearchDomainContext) {
  const baseFilters: ElasticsearchQuery[] = [validBettingSplitsQuery];

  if (!context.searchFlags.soccerBettingSplits) {
    baseFilters.push(noFootballQuery);
  }

  if (context.searchFlags.soccerEplOnly) {
    baseFilters.push(eplOnlySoccerQuery);
  }

  if (context.searchFlags.allWnba) {
    baseFilters.push(allWNBAQuery);
  }

  if (!context.searchFlags.newCategoryGroups) {
    baseFilters.push(supportedCategoryGroupsQuery);
  }

  if (!context.searchFlags.worldCup26) {
    baseFilters.push(noWorldCup26Query);
  }

  return baseFilters;
}

export function getBettingSplitsFacetAttributes(context: SearchDomainContext): FacetFieldConfig[] {
  const repubCountry = context.repubCountry;
  if (!repubCountry) return [];
  const areInternalSearchToolsEnabled = context.searchFlags.internalSearchTools;

  const internalSearchToolFacets: FacetFieldConfig[] = areInternalSearchToolsEnabled
    ? [{ attribute: 'bookmakerCode', field: 'bookmakerCode', type: 'string' }]
    : [];

  return [
    ...internalSearchToolFacets,
    {
      attribute: 'marketDisplayName',
      field: `marketDisplayNameMap.${repubCountry}.keyword`,
      type: 'string',
    },
    { attribute: 'eventDisplayName', field: 'eventDisplayName', type: 'string' },
    { attribute: 'subeventId', field: 'subeventId', type: 'numeric' },
    { attribute: 'subeventStartDate', field: 'subeventStartDate', type: 'string' },
    { attribute: 'subeventStartTime', field: 'subeventStartTime', type: 'string' },
    { attribute: 'teamData.away.name', field: 'teamData.away.name.keyword', type: 'string' },
    { attribute: 'teamData.away.nickName', field: 'teamData.away.nickName.keyword', type: 'string' },
    { attribute: 'teamData.away.shortName', field: 'teamData.away.shortName.keyword', type: 'string' },
    { attribute: 'teamData.home.name', field: 'teamData.home.name.keyword', type: 'string' },
    { attribute: 'teamData.home.nickName', field: 'teamData.home.nickName.keyword', type: 'string' },
    { attribute: 'teamData.home.shortName', field: 'teamData.home.shortName.keyword', type: 'string' },
  ];
}

export function getBettingSplitsSearchSettings() {
  const searchAttributes = [
    'teamData.away.name',
    'teamData.away.nickName',
    'teamData.away.shortName',
    'teamData.home.name',
    'teamData.home.nickName',
    'teamData.home.shortName',
  ];

  return {
    highlights: searchAttributes,
    results: bettingSplitsResultAttributes,
    search: searchAttributes,
  };
}

export function getBettingSplitsSorting(context: SearchUiContext): SearchRuntimeSortingConfig {
  const repubName = context.repubConfig.name;
  const translations = FILTERS_CONFIGS[repubName as keyof typeof FILTERS_CONFIGS] ?? FILTERS_CONFIGS.OC;
  return {
    options: [
      { field: 'subeventStartTime', label: translations.sortSubEventBetTime, order: 'asc' },
      { field: 'betPercentage', label: translations.sortBetPercentageHL, order: 'desc' },
      { field: 'betPercentage', label: translations.sortBetPercentageLH, order: 'asc' },
      { field: 'stakePercentage', label: translations.sortStakePercentageHL, order: 'desc' },
      { field: 'stakePercentage', label: translations.sortStakePercentageLH, order: 'asc' },
      { field: 'stakeBetDiff', label: translations.sortStakeBetDiffHL, order: 'desc' },
      { field: 'stakeBetDiff', label: translations.sortStakeBetDiffLH, order: 'asc' },
    ],
  };
}
